import React, { useMemo } from 'react';
import { formatTemp, getWeatherIcon, formatTime, getDayName, filterForecastDays, DEGREE_SYMBOL } from '../../utils/weatherUtils';
import Icon from '../../utils/icons';
import './TVLocationCard.css';

const getAqiColor = (aqi) => {
  const colors = { 1: '#4caf50', 2: '#8bc34a', 3: '#ffc107', 4: '#ff9800', 5: '#f44336' };
  return colors[aqi] || '#999';
};

const getAqiLabel = (aqi) => {
  const labels = { 1: 'Good', 2: 'Fair', 3: 'Moderate', 4: 'Poor', 5: 'Very Poor' };
  return labels[aqi] || '—';
};

function TVLocationCard({ location, data, aqi, size = 'large', isActive = false, onClick }) {
  if (!data?.current) {
    return (
      <div className={`tv-location-card ${size} loading`} onClick={onClick}>
        <div className="tv-card-loading">
          <div className="tv-card-spinner"></div>
          <span>{location}</span>
        </div>
      </div>
    );
  }

  const { current, forecast, hourly } = data;
  const weather = current;

  return (
    <div 
      className={`tv-location-card ${size} ${isActive ? 'active' : ''}`}
      onClick={onClick}
    >
      {/* Location Header */}
      <div className="tv-card-header">
        <div className="tv-card-location">
          <Icon name="mapPin" size={size === 'large' ? 28 : 20} />
          <div className="tv-card-location-text">
            <h2 className="tv-card-city">{weather.city}</h2>
            <span className="tv-card-country">{weather.country}</span>
          </div>
        </div>
        {size === 'large' && (
          <div className="tv-card-times">
            <div className="tv-card-sun">
              <Icon name="sunrise" size={18} />
              <span>{formatTime(weather.sunrise)}</span>
            </div>
            <div className="tv-card-sun">
              <Icon name="sunset" size={18} />
              <span>{formatTime(weather.sunset)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Weather Display */}
      <div className="tv-card-main">
        <div className="tv-card-temp-section">
          {weather.condition?.icon && (
            <img
              src={getWeatherIcon(weather.condition.icon, '4x')}
              alt={weather.condition?.description}
              className="tv-card-icon"
            />
          )}
          <div className="tv-card-temp-info">
            <span className="tv-card-temp">
              {Math.round(weather.current?.temperature || 0)}
              <span className="tv-card-temp-unit">{DEGREE_SYMBOL}F</span>
            </span>
            <span className="tv-card-feels">
              Feels like {formatTemp(weather.current?.feels_like)}
            </span>
          </div>
        </div>
        <div className="tv-card-condition">
          {weather.condition?.description}
        </div>
      </div>

      {/* Metrics */}
      {(size === 'large' || size === 'medium') && (
        <div className="tv-card-metrics">
          <div className="tv-metric">
            <Icon name="droplet" size={size === 'large' ? 24 : 18} />
            <div className="tv-metric-info">
              <span className="tv-metric-value">{weather.current?.humidity}%</span>
              <span className="tv-metric-label">Humidity</span>
            </div>
          </div>
          <div className="tv-metric">
            <Icon name="wind" size={size === 'large' ? 24 : 18} />
            <div className="tv-metric-info">
              <span className="tv-metric-value">{Math.round(weather.current?.wind_speed)} mph</span>
              <span className="tv-metric-label">Wind</span>
            </div>
          </div>
          <div className="tv-metric">
            <Icon name="gauge" size={size === 'large' ? 24 : 18} />
            <div className="tv-metric-info">
              <span className="tv-metric-value">{weather.current?.pressure} hPa</span>
              <span className="tv-metric-label">Pressure</span>
            </div>
          </div>
          <div className="tv-metric">
            <Icon name="eye" size={size === 'large' ? 24 : 18} />
            <div className="tv-metric-info">
              <span className="tv-metric-value">
                {((weather.current?.visibility || 0) / 1000).toFixed(1)} km
              </span>
              <span className="tv-metric-label">Visibility</span>
            </div>
          </div>
          {aqi && (
            <div className="tv-metric">
              <Icon name="leaf" size={size === 'large' ? 24 : 18} style={{ color: getAqiColor(aqi.aqi) }} />
              <div className="tv-metric-info">
                <span className="tv-metric-value" style={{ color: getAqiColor(aqi.aqi) }}>{getAqiLabel(aqi.aqi)}</span>
                <span className="tv-metric-label">Air Quality</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compact Metrics for small cards */}
      {size === 'compact' && (
        <div className="tv-card-compact-metrics">
          <span><Icon name="droplet" size={14} /> {weather.current?.humidity}%</span>
          <span><Icon name="wind" size={14} /> {Math.round(weather.current?.wind_speed)} mph</span>
        </div>
      )}

      {/* Mini Forecast for large cards */}
      {size === 'large' && forecast?.days && (
        <div className="tv-card-mini-forecast">
          {filterForecastDays(forecast.days, 5).map((day, index) => (
            <div key={index} className="tv-mini-day">
              <span className="tv-mini-day-name">
                {getDayName(day.date)}
              </span>
              {day.condition?.icon && (
                <img
                  src={getWeatherIcon(day.condition.icon)}
                  alt=""
                  className="tv-mini-icon"
                />
              )}
              <div className="tv-mini-temps">
                <span className="tv-mini-high">{Math.round(day.temperature_max)}{DEGREE_SYMBOL}</span>
                <span className="tv-mini-low">{Math.round(day.temperature_min)}{DEGREE_SYMBOL}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TVLocationCard;